<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import {
  isStagePipelineComplete,
  LIFECYCLE_STEPS,
  stageToneOf,
} from '../../constants/listingRequestLifecycle'
import type { StatusChip } from '../../constants/listingRequestStatusChips'

// Status cell of a Sell Request row: a dotted pill naming where the request is,
// and a segmented bar with one segment per lifecycle step. The pill is the
// server's status chip (label + hue); the bar reads the backend's (`stageStep`,
// `stageState`) projection. A rejected / withdrawn request fills no segments:
// the pipeline stopped, so no progress is shown as made.
const props = defineProps<{
  chip: StatusChip
  state: string
  currentStep: number
}>()

const tone = computed(() => props.chip.tone)

const filledSteps = computed(() => {
  if (stageToneOf(props.state) === 'negative') return 0
  if (isStagePipelineComplete(props.state)) return LIFECYCLE_STEPS.length
  return props.currentStep
})

const progressLabel = computed(() => `Step ${filledSteps.value} of ${LIFECYCLE_STEPS.length}`)
</script>

<template>
  <Box direction="col" gap="9px" class="stage-status" :class="`stage-status--${tone}`">
    <Box flex align="center" gap="2" class="stage-status__pill">
      <Box is="span" class="stage-status__dot" aria-hidden="true" />
      <Text is="span" type="caption" weight="medium" color="inherit">{{ chip.label }}</Text>
    </Box>
    <Box
      flex
      align="center"
      gap="1"
      class="stage-status__bar"
      role="img"
      :aria-label="progressLabel"
    >
      <Box
        is="span"
        v-for="item in LIFECYCLE_STEPS"
        :key="item.step"
        class="stage-status__segment"
        :class="{ 'stage-status__segment--filled': item.step <= filledSteps }"
      />
    </Box>
  </Box>
</template>

<style scoped>
/* One pair of custom properties per tone; the pill, dot and segments all read
   them, so a tone is a single declaration block rather than four selectors. */
.stage-status--neutral {
  --tone-bg: var(--p-surface-100);
  --tone-fg: var(--p-text-muted-color);
  --tone-solid: var(--p-surface-400);
}

.stage-status--primary {
  --tone-bg: var(--p-primary-50);
  --tone-fg: var(--p-primary-700);
  --tone-solid: var(--p-primary-500);
}

.stage-status--success {
  --tone-bg: var(--p-success-50);
  --tone-fg: var(--p-success-700);
  --tone-solid: var(--p-success-500);
}

.stage-status--warning {
  --tone-bg: var(--p-warning-50);
  --tone-fg: var(--p-warning-700);
  --tone-solid: var(--p-warning-500);
}

.stage-status--danger {
  --tone-bg: var(--p-danger-50);
  --tone-fg: var(--p-danger-700);
  --tone-solid: var(--p-danger-500);
}

.stage-status--info {
  --tone-bg: var(--p-info-50);
  --tone-fg: var(--p-info-700);
  --tone-solid: var(--p-info-500);
}

/* The -700 text ramps are light-mode only; on the dark -50 surfaces they drop
   below legible contrast, so dark mode lifts the label to the -300 step. */
:global(.p-dark .stage-status--primary) {
  --tone-fg: var(--p-primary-300);
}

:global(.p-dark .stage-status--success) {
  --tone-fg: var(--p-success-300);
}

:global(.p-dark .stage-status--warning) {
  --tone-fg: var(--p-warning-400);
}

:global(.p-dark .stage-status--danger) {
  --tone-fg: var(--p-danger-300);
}

:global(.p-dark .stage-status--info) {
  --tone-fg: var(--p-info-300);
}

.stage-status {
  align-items: flex-start;
  min-width: 0;
}

.stage-status__pill {
  padding: 0.25rem 0.75rem 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--tone-bg);
  color: var(--tone-fg);
  gap: 0.375rem;
  white-space: nowrap;
}

.stage-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--tone-solid);
  flex-shrink: 0;
}

.stage-status__segment {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: var(--p-surface-200);
}

.stage-status__segment--filled {
  background: var(--tone-solid);
}
</style>
